import { addDoc, collection, deleteDoc, doc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/config/firebase';
import type { FluxoModeloExtra, IdModeloMensagem } from '../models/mensagensContato.modelos';

/** Modelo editado pelo SI (si_config_mensagens/{id}). Sem doc = vale o texto padrão. */
export interface ModeloMensagemSalvo {
  id: IdModeloMensagem;
  texto: string;
  /** Documentos do Portal do SI (si_publicacoes) que acompanham a mensagem. */
  documentoIds: string[];
}

/** Modelo criado pelo SI (doc com `extra: true` na mesma coleção, id gerado). */
export interface ModeloMensagemExtra {
  id: string;
  titulo: string;
  fluxo: FluxoModeloExtra;
  texto: string;
  documentoIds: string[];
}

/** Documento publicado no Portal do SI que pode ir junto na mensagem. */
export interface DocumentoParaMensagem {
  id: string;
  titulo: string;
  versao: number;
  /** URL do arquivo (abre sem login — quem está em admissão ainda não tem Digital) ou link externo. */
  url: string;
  /** Caminho no Storage (só arquivo enviado) — pro "Compartilhar com anexo". */
  path: string | null;
  contentType: string | null;
  nomeArquivo: string;
}

const COLECAO = 'si_config_mensagens';

export const mensagensContatoService = {
  async listarSalvos(): Promise<{ padroes: ModeloMensagemSalvo[]; extras: ModeloMensagemExtra[] }> {
    const snap = await getDocs(collection(db, COLECAO));
    const padroes: ModeloMensagemSalvo[] = [];
    const extras: ModeloMensagemExtra[] = [];
    snap.docs.forEach((d) => {
      const dados = d.data();
      const base = { texto: dados.texto ?? '', documentoIds: dados.documentoIds ?? [] };
      if (dados.extra) {
        extras.push({ id: d.id, titulo: dados.titulo ?? 'Sem título', fluxo: dados.fluxo ?? 'todos', ...base });
      } else {
        padroes.push({ id: d.id as IdModeloMensagem, ...base });
      }
    });
    extras.sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR'));
    return { padroes, extras };
  },

  async salvar(modelo: ModeloMensagemSalvo, por: { id: string; name: string }): Promise<void> {
    await setDoc(doc(db, COLECAO, modelo.id), {
      texto: modelo.texto,
      documentoIds: modelo.documentoIds,
      atualizadoEm: serverTimestamp(),
      atualizadoPor: por,
    });
  },

  /** Cria (sem id) ou atualiza um modelo do SI; devolve o id. */
  async salvarExtra(modelo: Omit<ModeloMensagemExtra, 'id'> & { id?: string }, por: { id: string; name: string }): Promise<string> {
    const { id, ...dados } = modelo;
    const registro = { ...dados, extra: true, atualizadoEm: serverTimestamp(), atualizadoPor: por };
    if (id) {
      await setDoc(doc(db, COLECAO, id), registro);
      return id;
    }
    return (await addDoc(collection(db, COLECAO), registro)).id;
  },

  /** "Restaurar o texto padrão" (apaga a edição) ou "Excluir modelo" (modelo do SI). */
  async restaurar(id: string): Promise<void> {
    await deleteDoc(doc(db, COLECAO, id));
  },

  /** Documentos publicados do Portal do SI (canal SI). */
  async listarDocumentos(): Promise<DocumentoParaMensagem[]> {
    const snap = await getDocs(
      query(collection(db, 'si_publicacoes'), where('status', '==', 'publicada'), where('canal', '==', 'si'))
    );
    return snap.docs
      .map((d): Record<string, any> => ({ id: d.id, ...d.data() }))
      .filter((p) => p.tipo === 'documento' && p.arquivo?.url)
      .map((p) => ({
        id: p.id,
        titulo: p.titulo as string,
        versao: (p.versao as number) ?? 1,
        url: p.arquivo.url as string,
        path: (p.arquivo.path as string | null) ?? null,
        contentType: (p.arquivo.contentType as string | null) ?? null,
        nomeArquivo: (p.arquivo.nome as string) ?? `${p.titulo}.pdf`,
      }))
      .sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR'));
  },

  /** Bytes do arquivo pro "Compartilhar com anexo" (via function — o navegador não baixa direto do Storage). */
  async baixarArquivo(path: string): Promise<{ base64: string; contentType: string }> {
    const fn = httpsCallable<{ path: string }, { base64: string; contentType: string }>(getFunctions(), 'baixarArquivoPublicacaoSi');
    return (await fn({ path })).data;
  },
};
